import { createBrowserRouter as routerCreateBrowserRouter } from 'react-router';
import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { createBrowserRouter as datadogCreateBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { isProdEnv } from 'utils/isDevEnv';

// RUM runs only in production and only when an application ID is configured
const isDatadogRumEnabled =
  isProdEnv() && Boolean(import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID);

// Datadog router names RUM views by route pattern (e.g. /loan/:id) instead of the raw URL
export const createBrowserRouter = isDatadogRumEnabled
  ? datadogCreateBrowserRouter
  : routerCreateBrowserRouter;

// % of all sessions tracked by RUM
const SESSION_SAMPLE_RATE = 20;
// % of the RUM-tracked sessions above that also record Session Replay
const SESSION_REPLAY_SAMPLE_RATE = 10;

// Query/hash params that can carry credentials or PII: legacy one-time login token,
// affiliate pre-filled application token + OAuth2/Keycloak (OIDC) redirect params
const SENSITIVE_URL_PARAMS =
  /([?&#])(token|crossLoginToken|apiToken|code|state|session_state|access_token|id_token|id_token_hint|refresh_token|login_hint)=[^&#]*/gi;

const redactUrl = (url: string) =>
  url.replace(SENSITIVE_URL_PARAMS, '$1$2=REDACTED');

const redactEvent = (event: RumEvent) => {
  event.view.url = redactUrl(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = redactUrl(event.view.referrer);
  }
  if (event.type === 'resource') {
    event.resource.url = redactUrl(event.resource.url);
  }
  if (event.type === 'error') {
    event.error.message = redactUrl(event.error.message);
    if (event.error.resource) {
      event.error.resource.url = redactUrl(event.error.resource.url);
    }
  }
  return true;
};

export default function initDatadogRum() {
  if (!isDatadogRumEnabled) return;

  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATADOG_SITE,
    service: import.meta.env.VITE_DATADOG_SERVICE_NAME,
    // Must match --release-version used in scripts/upload-sourcemaps.mjs
    version: import.meta.env.VITE_RELEASE_VERSION || undefined,
    env: import.meta.env.VITE_ENV,
    sessionSampleRate: SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    enablePrivacyForActionName: true,
    silentMultipleInit: true,
    plugins: [reactPlugin({ router: true })],
    beforeSend: redactEvent,
  });
}

export const setDatadogRumUser = (id: string) => {
  if (isDatadogRumEnabled) datadogRum.setUser({ id });
};

export const clearDatadogRumUser = () => {
  if (isDatadogRumEnabled) datadogRum.clearUser();
};
